/**
 * FluentUI Design Kit Tokens
 */

// Shared interface for both light and dark themes
import { FluentUIDesignKitTokens } from './token.types';
export type { FluentUIDesignKitTokens, ThemeColors } from './token.types';

/**
 * FluentUI Design Kit Tokens
 */
export const tokens: FluentUIDesignKitTokens = {
  spacingVerticalXXS: '2px',
  spacingVerticalXS: '4px',
  spacingVerticalSNudge: '6px',
  spacingVerticalS: '8px',
  spacingVerticalM: '12px',
  spacingVerticalL: '16px',
  spacingVerticalXL: '20px',
  spacingVerticalXXL: '24px',
  spacingVerticalXXXL: '32px',

  spacingHorizontalXXS: '2px',
  spacingHorizontalXS: '4px',
  spacingHorizontalSNudge: '6px',
  spacingHorizontalS: '8px',
  spacingHorizontalM: '12px',
  spacingHorizontalL: '16px',
  spacingHorizontalXL: '20px',
  spacingHorizontalXXL: '24px',
  spacingHorizontalXXXL: '32px',

  light: {
    // Surface colors
    colorNeutralBackground1: '#ffffff',
    colorNeutralBackground1Selected: '#e6f3ff',
    colorNeutralBackground1Hover: '#f5f5f5',
    colorNeutralBackground1Pressed: '#e0e0e0',
    colorNeutralBackground2: '#fafafa',
    colorNeutralBackground3: '#f5f5f5',
    colorNeutralBackground4: '#f0f0f0',
    colorNeutralBackground6: '#E6E6E6',

    // Text colors
    colorNeutralForeground1: '#242424',
    colorNeutralForeground1Disabled: '#bdbdbd',
    colorNeutralForeground2: '#424242',
    colorNeutralForeground3: '#616161',
    colorNeutralForegroundInverted: '#ffffff',
    colorNeutralForegroundStaticInverted: '#ffffff',
    colorNeutralGrey44: '#707070',

    // Border colors
    colorNeutralStroke1: '#d1d1d1',
    colorNeutralStroke1Disabled: '#f0f0f0',
    colorNeutralStroke2: '#e0e0e0',
    colorNeutralStrokeAccessible: '#616161',

    // Brand colors
    colorBrand60: '#4ba91f', // Very light brand color for subtle elements
    colorBrand70: '#389120', // Lighter brand color for hover states
    colorBrand80: '#26890D', // Primary brand color
    colorBrand90: '#1a5a0b', // Darker brand color for buttons/interactive elements
    colorBrand100: '#0f3006', // Very dark brand color for pressed states
    colorBrandShade10: '#21750B',
    colorBrandStroke: '#A2C993',

    colorGreenBackground: '#CEE3C5',
    colorGreenForeground: '#236911',
    colorGreenBorder: '#A2C993',
    colorBlueBackground: '#DEECF9',
    colorBlueForeground: '#0F6CBD',
    colorBlueBorder: '#9FCDEA',
    colorRedBackground: '#FDF3F4',
    colorRedForeground: '#B10E1C',
    colorRedBorder: '#EEACB2',
    colorDangerForeGround1: '#B10E1C',
    colorErrorForeground: '#BC2F32',
    colorYellowBackground: '#FFF4CE',
    colorYellowForeground: '#B87300',
    colorYellowBorder: '#FFB900',
    colorOrangeBackground: '#FFF9F5',
    colorOrangeForeground: '#8A3707',
    colorOrangeBorder: '#FDCFB4',
    colorMarigoldBackground: '#FEF7E6',
    colorMarigoldForeground: '#835b00',
    colorMarigoldBorder: '#F2D092',
    colorMarigoldForeground2: '#835B00',
    colorPinkBackground: '#f7c0e3',
    colorPinkForeground: '#80215d',
    colorPinkBorder: '#e43ba6',
    colorPurpleForeground: '#7840B8',
    colorPurpleBorder: '#7840B8',
    colorWarningBorder: '#FDCFB4',
    colorWarningForeground2: '#8A3707',

    // Badge specific colors
    colorNeutralBackground5: '#ebebeb',
    colorNeutralBackground3Pressed: '#d6d6d6',

    // Status colors
    colorPaletteGreenBackground3: '#54b054',
    colorPaletteYellowBackground3: '#f7d22a',
    colorPaletteRedBackground3: '#d74654',
    colorPaletteBlueBackground3: '#0078d4',

    //Category specific colors
    colorCalendar1: '#046a38',
    colorCalendar2: '#ba420b',
    colorCalendar3: '#005587',
    colorCalendar4: '#b51963',
    colorCalendar5: '#012169',
    colorCalendar6: '#926b00',
    colorCalendar7: '#c50f1f',
    colorCalendar8: '#007680',
    colorCalendar9: '#424242',
    colorCalendarPurple: '#631db2',

    brandJewelGradient: 'linear-gradient(180deg, #1c3d26 0%, #046a38 100%)',

    // Spinner colors
    spinnerTrackColor: '#26890D',
    spinnerTailColor: '#A2C993',

    colorSuccessBackground1: '#DBFBD3',
    colorSuccessBorder1: '#7DF060',
    colorSuccessForeground1: '#21750B',

    colorBrandBackground2: '#CEE3C5',
    colorBrandForeground2: '#236911',

    colorNeutralForeground4: '#707070',

    warningForeground1: '#BC4B09',
  },

  dark: {
    // Surface colors
    colorNeutralBackground1: '#292929',
    colorNeutralBackground1Selected: '#0f3a5f',
    colorNeutralBackground1Hover: '#2d2d2d',
    colorNeutralBackground1Pressed: '#3a3a3a',
    colorNeutralBackground2: '#1f1f1f',
    colorNeutralBackground3: '#141414',
    colorNeutralBackground4: '#424242',
    colorNeutralBackground6: '##33333',

    // Text colors
    colorNeutralForeground1: '#ffffff',
    colorNeutralForeground1Disabled: '#616161',
    colorNeutralForeground2: '#c7c7c7',
    colorNeutralForeground3: '#a6a6a6',
    colorNeutralForegroundInverted: '#000000',
    colorNeutralForegroundStaticInverted: '#ffffff',
    colorNeutralGrey44: '#999999',

    // Border colors
    colorNeutralStroke1: '#5a5a5a',
    colorNeutralStroke1Disabled: '#3a3a3a',
    colorNeutralStroke2: '#4a4a4a',
    colorNeutralStrokeAccessible: '#a6a6a6',

    // Brand colors
    colorBrand60: '#4ba91f', // Very light brand color for subtle elements
    colorBrand70: '#389120', // Lighter brand color for hover states
    colorBrand80: '#26890D', // Primary brand color
    colorBrand90: '#1a5a0b', // Darker brand color for buttons/interactive elements
    colorBrand100: '#0f3006', // Very dark brand color for pressed states
    colorBrandShade10: '#21750B',
    colorBrandStroke: '#A2C993',

    // Tags
    colorGreenBackground: '#0E3B0E',
    colorGreenForeground: '#6BB700',
    colorGreenBorder: '#4A7C4A',
    colorBlueBackground: '#0F2A44',
    colorBlueForeground: '#4CC2FF',
    colorBlueBorder: '#2B5A8C',
    colorRedBackground: '#381C1B',
    colorRedForeground: '#FF4B4B',
    colorRedBorder: '#8C4A4A',
    colorDangerForeGround1: '#DC626D',
    colorErrorForeground: '#D74654',
    colorYellowBackground: '#3D2F00',
    colorYellowForeground: '#EAA300',
    colorYellowBorder: '#8B6914',
    colorOrangeBackground: '#3D2F00',
    colorOrangeForeground: '#FCE100',
    colorOrangeBorder: '#8B6914',
    colorMarigoldBackground: '#3D2F00',
    colorMarigoldForeground: '#EAA300',
    colorMarigoldBorder: '#8B6914',
    colorMarigoldForeground2: '#835B00',
    colorPinkBackground: '#5a2847',
    colorPinkForeground: '#f7c0e3',
    colorPinkBorder: '#8b4a6f',
    colorPurpleForeground: '#7840B8',
    colorPurpleBorder: '#7840B8',
    colorWarningBorder: '#FDCFB4',
    colorWarningForeground2: '#8A3707',

    // Badge specific colors
    colorNeutralBackground5: '#3a3a3a',
    colorNeutralBackground3Pressed: '#4a4a4a',

    // Status colors
    colorPaletteGreenBackground3: '#6bb700',
    colorPaletteYellowBackground3: '#fce100',
    colorPaletteRedBackground3: '#ff4b4b',
    colorPaletteBlueBackground3: '#4cc2ff',

    //Category specific colors
    colorCalendar1: '#86bc25',
    colorCalendar2: '#ed8b00',
    colorCalendar3: '#00a3e0',
    colorCalendar4: '#ff449c',
    colorCalendar5: '#a0dcff',
    colorCalendar6: '#fccd00',
    colorCalendar7: '#ff5464',
    colorCalendar8: '#00abab',
    colorCalendar9: '#d6d6d6',
    colorCalendarPurple: '#b36fff',

    brandJewelGradient: 'linear-gradient(180deg, #1c3d26 0%, #046a38 100%)',

    // Spinner colors
    spinnerTrackColor: '#44952E',
    spinnerTailColor: '#1E4B12',

    colorSuccessBackground1: '#DBFBD3',
    colorSuccessBorder1: '#7DF060',
    colorSuccessForeground1: '#21750B',

    colorBrandBackground2: '#CEE3C5',
    colorBrandForeground2: '#236911',

    colorNeutralForeground4: '#707070',

    warningForeground1: '#F98845',
  },

  // Typography
  fontFamilyBase: '"Open Sans", "Inter", system-ui, -apple-system, sans-serif',
  fontSizeBase100: '10px',
  fontSizeBase200: '12px',
  fontSizeBase300: '14px',
  fontSizeBase400: '16px',
  fontSizeBase500: '18px',
  fontSizeBase600: '20px',
  fontSizeHero500: '20px',
  fontSizeHero900: '40px',
  fontSizeTitle3: '24px',
  fontWeightRegular: '400',
  fontWeightMedium: '500',
  fontWeightSemibold: '600',
  fontWeightBold: '700',
  lineHeightBase100: '14px',
  lineHeightBase200: '16px',
  lineHeightBase300: '20px',
  lineHeightBase400: '22px',
  lineHeightBase500: '24px',
  lineHeightBase600: '28px',
  lineHeightHero500: '28px',
  lineHeightHero900: '56px',
  lineHeightTitle3: '32px',

  // Border and shadows
  borderRadiusSmall: '2px',
  borderRadiusMedium: '4px',
  borderRadiusLarge: '6px',
  borderRadiusXLarge: '8px',
  borderRadiusXXLarge: '10px',
  borderRadiusXXXLarge: '12px',
  borderRadiusXXXXLarge: '16px',
  borderRadiusHuge: '20px',
  borderRadiusCircular: '999px',
  borderRadiusNone: '0px',

  // Shadow tokens
  shadow4: '0 2px 4px rgba(0, 0, 0, 0.14), 0 0px 2px rgba(0, 0, 0, 0.12)',
  shadow8: '0 4px 8px rgba(0, 0, 0, 0.14), 0 0px 2px rgba(0, 0, 0, 0.12)',
  shadow16: '0 8px 16px rgba(0, 0, 0, 0.14), 0 2px 4px rgba(0, 0, 0, 0.12)',
  shadow64: '0 32px 64px rgba(0, 0, 0, 0.24), 0 8px 16px rgba(0, 0, 0, 0.12)',

  // Stroke widths
  strokeWidthThin: '1px',

  // Component sizing
  buttonMinWidth: '96px',
};
